import type { MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import {
  formatDateTime,
  formatHours,
  spanMinutes,
} from '../../../helpers/timesheet'
import { ClockOutPendingLabel } from './ClockOutPendingLabel'
import type { TimesheetTimeClock } from './types'

/**
 * The slugs every tenant is seeded with. These are the only reasons the crew
 * app can show in the operator's own language — a tenant's custom reason has
 * only the one name the API returns for it. A tenant that has *renamed* a
 * seeded reason gets its own name back, not the translation: their word for
 * the reason beats ours.
 */
const VOID_REASON_MESSAGES: Record<string, MessageDescriptor> = {
  duplicate_entry: msg`Duplicate entry`,
  never_worked: msg`Never worked`,
  entered_in_error: msg`Entered in error`,
  wrong_operator: msg`Wrong operator`,
  wrong_day: msg`Wrong day`,
}

/** The seeded English names — a tenant name differing from these is a rename. */
const DEFAULT_REASON_NAMES: Record<string, string> = {
  duplicate_entry: 'Duplicate entry',
  never_worked: 'Never worked',
  entered_in_error: 'Entered in error',
  wrong_operator: 'Wrong operator',
  wrong_day: 'Wrong day',
}

/** Time-clock entry: task, start/end, duration (v1 TimeClockDetail). */
export const TimeClockDetail = ({
  timeClock,
}: {
  timeClock: TimesheetTimeClock
}) => {
  const { _ } = useLingui()
  const voided = Boolean(timeClock.voided)
  const tenantName = timeClock.voidReasonDetails?.name
  const voidReasonMessage = timeClock.voidReason
    ? VOID_REASON_MESSAGES[timeClock.voidReason]
    : undefined
  const isRenamed =
    tenantName != null &&
    tenantName !== DEFAULT_REASON_NAMES[timeClock.voidReason ?? '']
  const voidReasonLabel =
    voidReasonMessage && !isRenamed
      ? _(voidReasonMessage)
      : (tenantName ?? timeClock.voidReason ?? '')
  return (
    <div
      className={`tc-history-entry${voided ? ' tc-history-entry--voided' : ''}`}
      style={{
        borderLeftColor: voided
          ? undefined
          : timeClock.task?.color || '#c7c7cc',
      }}
    >
      <div className='tc-history-entry-task'>
        {timeClock.task?.name || _(msg`Unknown Task`)}
      </div>
      <div className='tc-history-entry-row'>
        <span className='tc-history-entry-start'>
          {formatDateTime(timeClock.startTime)}
        </span>
        <ClockOutPendingLabel
          endTime={timeClock.endTime}
          className='tc-history-entry-end'
        />
        <span className='tc-history-entry-dur'>
          {formatHours(spanMinutes(timeClock.startTime, timeClock.endTime))}
        </span>
      </div>
      {voided && (
        <div className='tc-history-entry-voided'>
          {timeClock.voidReason
            ? _(msg`Voided — ${voidReasonLabel}`)
            : _(msg`Voided`)}
        </div>
      )}
    </div>
  )
}
